/* ============================================================================
   sb-motion.js: every page behaviour the kit's sections ask for, keyed on
   data-sb-* attributes. No dependencies, no DOM generation, no build step.

   THE DOCTRINE (references/doctrine.md), enforced here:
   - Nothing on the page stops moving while the visitor scrolls. No pin, no
     sticky region, no scroll hijack. The rail and the arc take the wheel only
     while the pointer is over them; nothing else ever calls preventDefault on
     a wheel.
   - No transition started by a click or hover waits on requestAnimationFrame:
     a class is added to an element whose "before" style is already computed,
     and CSS does the rest. rAF is used only by loops that the wheel, a drag or
     the scroll drive, and they stop themselves on arrival.
   - Every effect binds to a wrapper the section draws, never to the merchant's
     picture or words, so swapping a picture in the theme editor keeps the effect.

   LIFECYCLE
   - SB.define(name, arm): register an effect for [data-sb-<name>].
   - SB.arm(root): arm every effect under root. Each element records what armed
     it, so arming twice is a no-op (shopify:section:load can fire for a section
     that is already armed).
   - The theme editor re-renders one section at a time and fires
     shopify:section:load on it: the engine is mounted on that section alone and
     its effects are armed again.
   ========================================================================== */
(function () {
  "use strict";

  var SB = (window.SB = window.SB || {});
  var effects = {};
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  SB.reduce = reduce;
  SB.finePointer = finePointer;

  SB.config = (function () {
    try {
      var el = document.getElementById("sb-config");
      return el ? JSON.parse(el.textContent) : {};
    } catch (err) {
      console.error("🛑 [sb-motion]: sb-config is not valid JSON", err);
      return {};
    }
  })();

  SB.define = function (name, arm) {
    effects[name] = arm;
  };

  function armedList(el) {
    return (el.getAttribute("data-sb-armed") || "").split(" ").filter(Boolean);
  }

  SB.arm = function (root) {
    root = root || document;
    Object.keys(effects).forEach(function (name) {
      var sel = "[data-sb-" + name + "]";
      var nodes = Array.prototype.slice.call(root.querySelectorAll(sel));
      if (root.matches && root.matches(sel)) nodes.unshift(root);
      nodes.forEach(function (el) {
        var done = armedList(el);
        if (done.indexOf(name) >= 0) return;
        done.push(name);
        el.setAttribute("data-sb-armed", done.join(" "));
        try {
          effects[name](el);
        } catch (err) {
          console.error("🛑 [sb-motion]: arming " + name + " failed", el, err);
        }
      });
    });
  };

  // A detached Image is the readiness gate for any swap: decode() on an invisible
  // element resolves up to a second late (traps.md §4).
  SB.ready = function (src, done) {
    var img = new Image();
    img.decoding = "async";
    img.src = src;
    if (img.complete) return done();
    img.onload = done;
    img.onerror = function () {
      console.error("🛑 [sb-motion]: image failed to load", src);
      done();
    };
  };

  SB.money = function (cents) {
    var fmt = SB.config.moneyFormat || "${{amount}}";
    var n = (Number(cents) || 0) / 100;
    var parts = n.toFixed(2).split(".");
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key === "amount_no_decimals") return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      if (key === "amount_with_comma_separator") return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + parts[1];
      return whole + "." + parts[1];
    });
  };

  /* -------------------------------------------------------------------------
     loop: a clip on its own clock (motion.md §1). Never touches the scroll.
     ------------------------------------------------------------------------- */
  SB.define("loop", function (host) {
    console.log("⚙️ [Function Started]: loop", host);
    if (reduce) {
      console.log("📦 [State Update]: reduced motion, the still holds and the clip is never fetched");
      return;
    }
    var phone = window.matchMedia("(max-width: 749px)").matches;
    var src = (phone && host.getAttribute("data-src-mobile")) || host.getAttribute("data-src");
    if (!src) return;
    var video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("aria-hidden", "true");
    video.preload = "auto";
    video.className = "sb-loop__video";
    video.src = src;
    host.appendChild(video);

    function play() {
      var p = video.play();
      if (p && p.catch) {
        p.catch(function (err) {
          console.log("📦 [State Update]: play refused (" + err.name + "), retrying on the first touch");
          var retry = function () {
            console.log("👆 [Trigger]: first touch, retrying the clip");
            video.play().catch(function (e) { console.error("🛑 [loop]: still refused", e); });
          };
          window.addEventListener("pointerdown", retry, { once: true, passive: true });
        });
      }
    }
    video.addEventListener("playing", function () {
      console.log("✅ [loop]: playing", src);
      host.classList.add("is-playing");
    });
    video.addEventListener("error", function () {
      console.error("🛑 [loop]: the clip failed to load, the still holds", src);
    });

    // Off screen, the clip rests: nothing runs that nobody sees.
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) play();
          else video.pause();
        });
      }, { threshold: 0.01 }).observe(host);
    } else play();
  });

  /* -------------------------------------------------------------------------
     header: solid once the first screen has gone by; hides while the visitor
     scrolls down, returns the moment they scroll up. Chrome, not content.
     ------------------------------------------------------------------------- */
  SB.define("header", function (header) {
    console.log("⚙️ [Function Started]: header");
    var hides = header.getAttribute("data-sb-header") === "hide";
    var lastY = window.scrollY;
    var hidden = false;
    var solid = null;
    var DEAD = 8;

    function solidLine() {
      return header.classList.contains("sb-header--overlay") ? Math.max(120, window.innerHeight * 0.6) : 1;
    }
    function update() {
      var y = window.scrollY;
      var nextSolid = y > solidLine();
      if (nextSolid !== solid) {
        solid = nextSolid;
        header.classList.toggle("is-solid", solid);
      }
      if (!hides) return;
      var dy = y - lastY;
      if (Math.abs(dy) < DEAD) return;
      var nextHidden = dy > 0 && y > header.offsetHeight * 2 && !header.contains(document.activeElement);
      if (nextHidden !== hidden) {
        hidden = nextHidden;
        header.classList.toggle("is-hidden", hidden);
        console.log("📦 [State Update]: header", hidden ? "hidden (scrolling down)" : "shown (scrolling up)");
      }
      lastY = y;
    }
    window.addEventListener("scroll", update, { passive: true });
    header.addEventListener("focusin", function () {
      hidden = false;
      header.classList.remove("is-hidden");
    });
    update();
  });

  /* -------------------------------------------------------------------------
     drop: a panel that opens on a press (never on hover), closes on Escape, on
     a press outside, or when another opens.
     ------------------------------------------------------------------------- */
  var openDrop = null;
  function closeDrop(drop) {
    if (!drop) return;
    var t = drop.querySelector("[aria-expanded]");
    var panel = drop.querySelector(".sb-drop__panel");
    if (t) t.setAttribute("aria-expanded", "false");
    if (panel) panel.hidden = true;
    if (openDrop === drop) openDrop = null;
  }
  SB.define("drop", function (drop) {
    var toggle = drop.querySelector("[aria-expanded]");
    var panel = drop.querySelector(".sb-drop__panel");
    if (!toggle || !panel) return;
    toggle.addEventListener("click", function () {
      var open = toggle.getAttribute("aria-expanded") === "true";
      console.log("👆 [Trigger]: dropdown", open ? "closed" : "opened", toggle.textContent.trim());
      if (openDrop && openDrop !== drop) closeDrop(openDrop);
      toggle.setAttribute("aria-expanded", String(!open));
      panel.hidden = open;
      openDrop = open ? null : drop;
    });
  });
  document.addEventListener("click", function (e) {
    if (openDrop && !openDrop.contains(e.target)) closeDrop(openDrop);
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && openDrop) {
      var t = openDrop.querySelector("[aria-expanded]");
      closeDrop(openDrop);
      if (t) t.focus();
    }
  });

  /* -------------------------------------------------------------------------
     autosubmit: a GET form (the sort) that applies itself on change.
     ------------------------------------------------------------------------- */
  SB.define("autosubmit", function (form) {
    form.addEventListener("change", function () {
      console.log("👆 [Trigger]: sort changed, applying");
      form.submit();
    });
  });

  /* -------------------------------------------------------------------------
     crossfade: the upper photo rests at opacity 0, so adding a class fades it
     with no rAF (motion.md §3). The lower one takes the new src under it, then
     the upper one leaves. Measured on VANTA: 328-483ms from click to rest.
     ------------------------------------------------------------------------- */
  SB.crossfade = function (stage, src, alt) {
    var base = stage.querySelector("[data-sb-stage-base]");
    var next = stage.querySelector("[data-sb-stage-next]");
    if (!base || !next || !src) return;
    if (base.getAttribute("src") === src) return;
    clearTimeout(stage.__sbSwap);
    SB.ready(src, function () {
      next.src = src;
      next.classList.add("on");
      stage.__sbSwap = setTimeout(function () {
        base.removeAttribute("srcset");
        base.src = src;
        if (alt !== undefined) base.alt = alt;
        stage.__sbSwap = setTimeout(function () {
          next.classList.remove("on");
          console.log("✅ [crossfade]: at rest", src);
        }, 60);
      }, reduce ? 20 : 320);
    });
  };

  /* -------------------------------------------------------------------------
     product: the variant picker. Choosing a colour or size finds the variant,
     updates the price, the hidden id, the Add button and the URL, and swaps the
     photo group when the colour changes.
     ------------------------------------------------------------------------- */
  SB.define("product", function (root) {
    console.log("⚙️ [Function Started]: product", root.getAttribute("data-section-id"));
    var dataEl = root.querySelector("[data-sb-product-json]");
    if (!dataEl) return;
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error("🛑 [product]: product JSON is invalid", err);
      return;
    }
    var form = root.querySelector("[data-sb-add-form]");
    var stage = root.querySelector("[data-sb-crossfade]");
    var thumbs = root.querySelector("[data-sb-thumbs]");
    var idInput = root.querySelector("[data-sb-variant-input]");
    var button = root.querySelector("[data-sb-add-button]");
    var label = root.querySelector("[data-sb-add-label]");
    var priceEl = root.querySelector("[data-sb-variant-price]");
    // The colour is drawn first whatever the supplier's option order; the code works in the
    // product's own option order, which each fieldset carries in data-sb-option.
    var fieldsets = Array.prototype.slice.call(root.querySelectorAll("[data-sb-option]")).sort(function (a, b) {
      return Number(a.getAttribute("data-sb-option")) - Number(b.getAttribute("data-sb-option"));
    });
    var strings = SB.config.strings || {};
    var starts = data.variants.map(function (v) { return v.mediaPosition; }).filter(Boolean).sort(function (a, b) { return a - b; });

    function chosen() {
      return fieldsets.map(function (fs) {
        var c = fs.querySelector("input:checked");
        return c ? c.value : null;
      });
    }
    function find(opts) {
      return data.variants.find(function (v) {
        return opts.every(function (o, i) { return o === null || v.options[i] === o; });
      });
    }
    function group(start) {
      var end = Infinity;
      starts.forEach(function (s) { if (s > start && s < end) end = s; });
      return data.media.filter(function (m) { return m.position >= start && m.position < end; });
    }
    // A colour's own photos: the pair the page names for it (data.views, from snippets/sb-views),
    // else the run from its variant's photo to the next colour's.
    function viewsOf(v) {
      if (data.views && data.colorIndex >= 0) {
        var pos = data.views[v.options[data.colorIndex]] || [];
        return pos.map(function (p) { return data.media.find(function (m) { return m.position === p; }); }).filter(Boolean);
      }
      return v.mediaPosition ? group(v.mediaPosition) : [];
    }
    function paintThumbs(list) {
      if (!thumbs) return;
      thumbs.innerHTML = "";
      list.forEach(function (m, i) {
        var li = document.createElement("li");
        var b = document.createElement("button");
        b.type = "button";
        b.className = "sb-gallery__thumb sb-plate";
        b.setAttribute("data-sb-view", m.src);
        b.setAttribute("aria-pressed", i === 0 ? "true" : "false");
        b.setAttribute("aria-label", "View " + (i + 1));
        var img = document.createElement("img");
        img.src = m.thumb;
        img.alt = "";
        img.loading = "lazy";
        b.appendChild(img);
        li.appendChild(b);
        thumbs.appendChild(li);
      });
    }
    function availability(opts) {
      // A size that does not exist in the chosen colour is struck through.
      fieldsets.forEach(function (fs, i) {
        Array.prototype.forEach.call(fs.querySelectorAll("input"), function (input) {
          var trial = opts.slice();
          trial[i] = input.value;
          var v = find(trial);
          if (v && v.available) input.removeAttribute("data-sb-unavailable");
          else input.setAttribute("data-sb-unavailable", "");
        });
      });
    }

    var lastColour = data.colorIndex >= 0 ? chosen()[data.colorIndex] : null;

    // A colour that does not come in the chosen size (White and Grey stop at XL on some tees): keep the
    // colour and move to the nearest size it has, so its photos and price show at once instead of
    // nothing happening (the owner, 2026-10-23).
    function settle(opts) {
      if (data.colorIndex < 0 || opts[data.colorIndex] === lastColour) return opts;
      var colour = opts[data.colorIndex];
      var same = data.variants.filter(function (x) { return x.options[data.colorIndex] === colour; });
      if (!same.length) return opts;
      function order(i, value) {
        var inputs = Array.prototype.slice.call(fieldsets[i].querySelectorAll("input"));
        for (var k = 0; k < inputs.length; k++) if (inputs[k].value === value) return k;
        return 0;
      }
      function distance(x) {
        var d = 0;
        opts.forEach(function (o, i) { if (i !== data.colorIndex && o !== null) d += Math.abs(order(i, x.options[i]) - order(i, o)); });
        return (x.available ? 0 : 1000) + d;
      }
      var pick = same.slice().sort(function (a, b) { return distance(a) - distance(b); })[0];
      fieldsets.forEach(function (fs, i) {
        Array.prototype.forEach.call(fs.querySelectorAll("input"), function (input) {
          if (input.value === pick.options[i]) input.checked = true;
        });
      });
      console.log("📦 [State Update]:", colour, "has no", opts.filter(function (o, i) { return i !== data.colorIndex; }).join(" / "), "→", pick.options.join(" / "));
      return chosen();
    }

    function apply(reason) {
      var opts = chosen();
      var v = find(opts);
      if (!v) {
        opts = settle(opts);
        v = find(opts);
      }
      console.log("📦 [State Update]: options", opts.join(" / "), "→ variant", v ? v.id : "none", "(" + reason + ")");
      fieldsets.forEach(function (fs, i) {
        var out = fs.querySelector("[data-sb-option-chosen]");
        if (out) out.textContent = opts[i] || "";
      });
      availability(opts);
      if (!v) {
        if (button) button.disabled = true;
        if (label) label.textContent = strings.choose || "Choose";
        return;
      }
      if (idInput) idInput.value = v.id;
      if (button) button.disabled = !v.available;
      if (label) label.textContent = v.available ? strings.add || "Add" : strings.soldOut || "Sold out";
      if (priceEl) {
        priceEl.innerHTML = '<span class="sb-price"><span class="sb-price__now">' + SB.money(v.price) + "</span>" +
          (v.compare > v.price ? '<s class="sb-price__was">' + SB.money(v.compare) + "</s>" : "") + "</span>";
      }
      var url = new URL(window.location.href);
      url.searchParams.set("variant", v.id);
      window.history.replaceState({}, "", url);

      var colour = data.colorIndex >= 0 ? opts[data.colorIndex] : null;
      var list = colour !== lastColour ? viewsOf(v) : [];
      if (colour !== lastColour && list.length) {
        lastColour = colour;
        console.log("📦 [State Update]: colour", colour, "shows", list.length, "photo(s)");
        if (list.length && stage) {
          if (stage.getAttribute("data-sb-colour-change") === "dye") SB.dye(stage, list[0].src, list[0].alt);
          else SB.crossfade(stage, list[0].src, list[0].alt);
          paintThumbs(list);
        }
      }
    }

    fieldsets.forEach(function (fs) {
      fs.addEventListener("change", function () {
        console.log("👆 [Trigger]: option changed", fs.getAttribute("data-sb-option"));
        apply("option");
      });
    });
    if (thumbs) {
      thumbs.addEventListener("click", function (e) {
        var b = e.target.closest("[data-sb-view]");
        if (!b) return;
        console.log("👆 [Trigger]: view chosen", b.getAttribute("data-sb-view"));
        Array.prototype.forEach.call(thumbs.querySelectorAll("[data-sb-view]"), function (x) { x.setAttribute("aria-pressed", x === b ? "true" : "false"); });
        SB.crossfade(stage, b.getAttribute("data-sb-view"));
      });
    }
    // Warm each colour's FIRST photo while the page is idle: a colour change shows exactly that photo,
    // so it stays instant. A colour's other views load when it is chosen. Warming every view of every
    // colour cost 2.5 MB on a ten-colour tee (2026-10-05 review); the first photos cost ~0.3 MB.
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
    idle(function () {
      var first = {};
      starts.forEach(function (p) { first[p] = true; });
      var warm = data.media.filter(function (m) { return first[m.position]; });
      warm.forEach(function (m) { var i = new Image(); i.src = m.src; });
      console.log("✅ [product]: warmed", warm.length, "colours' first photos of", data.media.length);
    });
    availability(chosen());
    if (form) form.setAttribute("data-sb-ready", "");
  });

  /* -------------------------------------------------------------------------
     scroll watchers: effects that READ the scroll position (never take it).
     One passive listener for all of them; each watcher runs only while its
     element is near the screen. No rAF: the scroll event already arrives once
     per frame, and work is written straight to CSS variables.
     ------------------------------------------------------------------------- */
  var watchers = [];
  function watch(el, fn) {
    var w = { el: el, fn: fn, live: true };
    watchers.push(w);
    if ("IntersectionObserver" in window) {
      w.live = false;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { w.live = e.isIntersecting; if (w.live) fn(); });
      }, { rootMargin: "50% 0px 50% 0px" }).observe(el);
    }
    fn();
    return w;
  }
  function onScroll() {
    for (var i = 0; i < watchers.length; i++) {
      var w = watchers[i];
      if (w.live && w.el.isConnected) w.fn();
    }
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* -------------------------------------------------------------------------
     rail: the one region allowed to take the wheel, and only while the pointer
     is over it (motion.md §2, measured on VANTA). A wheel notch moves a TARGET
     and the strip glides to it; drag is 1:1; a flick carries on; Left/Right
     step; Up/Down/Page/Space keep scrolling the page. Endless: clones fill the
     window, stripped of ids and focus. data-sb-rail="arc" lays the items on a
     curve (Beautify: rotate 8.1°·k, drop 32px·k², k = slots from centre).
     ------------------------------------------------------------------------- */
  SB.define("rail", function (car) {
    var mode = car.getAttribute("data-sb-rail") || "line";
    var track = car.querySelector("[data-sb-rail-track]");
    if (!track) return;
    console.log("⚙️ [Function Started]: rail", mode);
    // A finger gets the phone's own scrolling: its glide and weight, resting on a card (cubed-phone.css).
    // The drag below follows a mouse well, but a flick on glass read as too fast and jumpy (the owner,
    // 2026-10-19). A mouse, a trackpad and a touch laptop's pointer keep the endless strip. Which one it
    // is, theme.liquid has said on <html> (cubed-finger): Galaxy phones claim a mouse to the media query.
    var finger = document.documentElement.classList.contains("cubed-finger") ||
      window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (reduce || finger) {
      car.classList.add("is-plain");
      console.log(reduce
        ? "📦 [State Update]: reduced motion, the rail is a plain scroll region and the wheel is left alone"
        : "📦 [State Update]: a finger, the rail scrolls natively");
      var plainHost = car.closest("[data-sb-rail-host]") || car.parentElement;
      Array.prototype.slice.call(plainHost.querySelectorAll("[data-sb-rail-step]")).forEach(function (btn) {
        var dir = btn.getAttribute("data-sb-rail-step") === "next" ? 1 : -1;
        btn.addEventListener("click", function (ev) {
          ev.preventDefault();
          var first = track.firstElementChild;
          var step = first ? first.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : car.clientWidth * 0.8;
          console.log("👆 [Trigger]: rail stepped by arrow", dir > 0 ? "next" : "prev");
          car.scrollBy({ left: dir * step, behavior: reduce ? "auto" : "smooth" });
        });
      });
      return;
    }
    car.setAttribute("data-sb-takes-wheel", "");
    var originals = Array.prototype.slice.call(track.children);
    var setW = 0, offset = 0, target = 0, vel = 0, raf = null, stops = [], slot = 1;
    var speed = parseFloat(car.getAttribute("data-speed")) || 0.8;
    var LERP = 0.14;
    var stage = car.closest("[data-sc-act]") || car;

    function measure() {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0, run = 0;
      stops = [];
      originals.forEach(function (el) { stops.push(run); run += el.getBoundingClientRect().width + gap; });
      slot = originals.length ? run / originals.length : 1;
      return run;
    }
    function stepTo(dir) {
      if (!stops.length || setW <= 0) return target;
      var here = ((target % setW) + setW) % setW, base = target - here, i, to = null;
      if (dir > 0) {
        for (i = 0; i < stops.length; i++) if (stops[i] > here + 1) { to = stops[i]; break; }
        if (to === null) return base + setW + stops[0];
      } else {
        for (i = stops.length - 1; i >= 0; i--) if (stops[i] < here - 1) { to = stops[i]; break; }
        if (to === null) return base - setW + stops[stops.length - 1];
      }
      return base + to;
    }
    function build() {
      Array.prototype.slice.call(track.querySelectorAll("[data-clone]")).forEach(function (n) { n.remove(); });
      setW = measure();
      if (setW <= 0) { console.error("🛑 [rail]: nothing to measure"); return; }
      var copies = Math.ceil(car.clientWidth / setW) + 1;
      for (var c = 0; c < copies; c++) {
        originals.forEach(function (el) {
          var k = el.cloneNode(true);
          k.setAttribute("data-clone", "");
          k.setAttribute("aria-hidden", "true");
          k.removeAttribute("id");
          Array.prototype.slice.call(k.querySelectorAll("[id]")).forEach(function (n) { n.removeAttribute("id"); });
          Array.prototype.slice.call(k.querySelectorAll("a,button,select,input")).forEach(function (n) { n.setAttribute("tabindex", "-1"); });
          track.appendChild(k);
        });
      }
      console.log("📦 [State Update]: rail built,", originals.length, "items ×", copies + 1, "sets of", Math.round(setW) + "px");
      target = offset;
      render();
    }
    var items = function () { return track.children; };
    function render() {
      if (setW <= 0) return;
      var x = -(((offset % setW) + setW) % setW);
      track.style.transform = "translate3d(" + x.toFixed(2) + "px,0,0)";
      if (mode === "arc") {
        var mid = car.clientWidth / 2, list = items();
        for (var i = 0; i < list.length; i++) {
          var it = list[i];
          var centre = it.offsetLeft + it.offsetWidth / 2 + x;
          var k = (centre - mid) / slot;
          if (Math.abs(k) > 4) continue;
          it.style.transform = "translateY(" + (32 * k * k).toFixed(1) + "px) rotate(" + (8.1 * k).toFixed(2) + "deg)";
        }
      }
      // The harness reads travel here, or it would call this band dead scroll.
      stage.setAttribute("data-sc-verify-state", "rail:" + Math.round(-x));
    }
    function tick() {
      var d = target - offset;
      if (Math.abs(d) < 0.15) { offset = target; render(); raf = null; return; }
      offset += d * LERP;
      render();
      // sb-allow-raf: wheel/drag-driven glide that stops itself on arrival (motion.md §2)
      raf = requestAnimationFrame(tick);
    }
    function run() {
      // sb-allow-raf: starts the glide above
      if (raf === null) raf = requestAnimationFrame(tick);
    }

    // sb-takes-wheel: only while the pointer is over the strip; the band is as tall as the track
    car.addEventListener("wheel", function (ev) {
      ev.preventDefault();
      var d = Math.abs(ev.deltaY) >= Math.abs(ev.deltaX) ? ev.deltaY : ev.deltaX;
      if (ev.deltaMode === 1) d *= 16;
      target += d * speed;
      run();
    }, { passive: false });

    var grab = null;
    car.addEventListener("pointerdown", function (ev) {
      if (ev.pointerType === "mouse" && ev.button !== 0) return;
      if (ev.target.closest("[data-sb-rail-step],[data-sb-add-form],button")) return;
      grab = { x: ev.clientX, id: ev.pointerId, moved: 0, at: performance.now() };
      vel = 0;
      if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
      target = offset;
    });
    car.addEventListener("pointermove", function (ev) {
      if (!grab || grab.id !== ev.pointerId) return;
      var dx = ev.clientX - grab.x;
      grab.x = ev.clientX;
      grab.moved += Math.abs(dx);
      if (!grab.captured && grab.moved > 6) {
        grab.captured = true;
        try { car.setPointerCapture(ev.pointerId); } catch (e) { console.error("🛑 [rail]: could not take the pointer", e); }
      }
      offset -= dx;
      target = offset;
      var now = performance.now();
      vel = (-dx / Math.max(now - grab.at, 1)) * 16;
      grab.at = now;
      render();
    });
    car.addEventListener("pointerup", function (ev) {
      if (!grab || grab.id !== ev.pointerId) return;
      if (grab.moved > 6) {
        car.dataset.dragged = "1";
        console.log("👆 [Trigger]: rail dragged", Math.round(grab.moved) + "px");
      }
      grab = null;
      target = offset + vel * 6;
      run();
    });
    car.addEventListener("pointercancel", function () { grab = null; vel = 0; target = offset; });
    car.addEventListener("click", function (ev) {
      if (car.dataset.dragged === "1") { ev.preventDefault(); ev.stopPropagation(); delete car.dataset.dragged; }
    }, true);
    car.addEventListener("keydown", function (ev) {
      if (ev.key !== "ArrowRight" && ev.key !== "ArrowLeft") return;
      ev.preventDefault();
      console.log("👆 [Trigger]: rail stepped by keyboard", ev.key);
      target = stepTo(ev.key === "ArrowRight" ? 1 : -1);
      run();
    });
    var host = car.closest("[data-sb-rail-host]") || car.parentElement;
    Array.prototype.slice.call(host.querySelectorAll("[data-sb-rail-step]")).forEach(function (btn) {
      var dir = btn.getAttribute("data-sb-rail-step") === "next" ? 1 : -1;
      btn.addEventListener("click", function (ev) {
        ev.preventDefault();
        console.log("👆 [Trigger]: rail stepped by arrow", dir > 0 ? "next" : "prev");
        target = stepTo(dir);
        run();
      });
    });
    var resizeT;
    window.addEventListener("resize", function () { clearTimeout(resizeT); resizeT = setTimeout(build, 150); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(build);
    build();
  });

  /* -------------------------------------------------------------------------
     marquee: a strip that runs on its own clock, forever. Pure CSS keyframes on
     transform; this script only fills the track twice over and sets the speed
     in px/s so a long strip and a short one move at the same pace.
     ------------------------------------------------------------------------- */
  SB.define("marquee", function (box) {
    var track = box.querySelector("[data-sb-marquee-track]");
    if (!track) return;
    var vertical = box.getAttribute("data-direction") === "up" || box.getAttribute("data-direction") === "down";
    console.log("⚙️ [Function Started]: marquee", box.getAttribute("data-direction") || "left");
    if (reduce) { box.classList.add("is-still"); return; }
    var originals = Array.prototype.slice.call(track.children);
    function fill() {
      Array.prototype.slice.call(track.querySelectorAll("[data-clone]")).forEach(function (n) { n.remove(); });
      var one = vertical ? track.scrollHeight : track.scrollWidth;
      var need = vertical ? box.clientHeight : box.clientWidth;
      if (!one) return;
      var copies = Math.max(1, Math.ceil(need / one));
      // Twice the visible run, so translating by -50% loops with no seam.
      for (var c = 0; c < copies * 2 - 1; c++) {
        originals.forEach(function (el) {
          var k = el.cloneNode(true);
          k.setAttribute("data-clone", "");
          k.setAttribute("aria-hidden", "true");
          Array.prototype.slice.call(k.querySelectorAll("a,button")).forEach(function (n) { n.setAttribute("tabindex", "-1"); });
          track.appendChild(k);
        });
      }
      var half = (vertical ? track.scrollHeight : track.scrollWidth) / 2;
      var pxs = parseFloat(box.getAttribute("data-speed")) || 60;
      box.style.setProperty("--sb-marquee-dur", (half / pxs).toFixed(2) + "s");
      box.classList.add("is-running");
    }
    var t;
    window.addEventListener("resize", function () { clearTimeout(t); t = setTimeout(fill, 200); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fill);
    fill();
  });

  /* -------------------------------------------------------------------------
     sides: words and pictures that arrive from the left and right as the section
     comes up the screen (Beautify's "Flawless finish"). Driven by scroll
     POSITION, never holding it, and a 0.2s transform transition smooths the
     steps between scroll events. The words and the picture each run their own
     --sb-p from 0, when they come up over the screen's lower edge, to 1, when
     the middle of the words reaches the middle of the screen: the whole
     movement happens in view and ends with the words centred. The picture
     starts RISE of a screen lower than its place, so it counts from when that
     lowered picture comes into view.
     ------------------------------------------------------------------------- */
  SB.define("sides", function (sec) {
    console.log("⚙️ [Function Started]: sides");
    if (reduce) { sec.style.setProperty("--sb-p", "1"); return; }
    var RISE = 0.15; // [data-sb-from="below"] in sb-motion.css starts 15vh low
    var words = sec.querySelector(".sb-sides__lines");
    if (!words) return;
    var groups = [words].concat(Array.prototype.slice.call(sec.querySelectorAll(".sb-sides__stage")));
    var last = groups.map(function () { return -1; });
    watch(sec, function () {
      var w = words.getBoundingClientRect();
      var toLand = w.top + w.height / 2 - innerHeight / 2;
      for (var i = 0; i < groups.length; i++) {
        var top = i === 0 ? w.top : groups[i].getBoundingClientRect().top + RISE * innerHeight;
        var risen = innerHeight - top, run = risen + toLand;
        var p = run > 0 ? clamp(risen / run, 0, 1) : (toLand <= 0 ? 1 : 0);
        p = Math.round(p * 1000) / 1000;
        if (p !== last[i]) { last[i] = p; groups[i].style.setProperty("--sb-p", p); }
      }
    });
  });

  /* -------------------------------------------------------------------------
     drum: a column of cards on a turning drum (stride's portfolio), WITHOUT the
     pin. Each card's tilt, scale and light come from its own distance to the
     middle of the screen, so the page keeps moving and the cards turn as they
     pass. The glow behind takes the colour of the card at the centre.
     ------------------------------------------------------------------------- */
  SB.define("drum", function (col) {
    var cards = Array.prototype.slice.call(col.querySelectorAll("[data-sb-drum-card]"));
    console.log("⚙️ [Function Started]: drum", cards.length, "cards");
    if (reduce || !cards.length) return;
    var tilt = parseFloat(col.getAttribute("data-tilt")) || 34;
    var lastGlow = null;
    watch(col, function () {
      var mid = innerHeight / 2, best = null, bestD = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i], r = c.getBoundingClientRect();
        var d = clamp((r.top + r.height / 2 - mid) / (innerHeight * 0.62), -1.4, 1.4);
        var a = Math.abs(d);
        c.style.setProperty("--sb-d", d.toFixed(3));
        c.style.transform = "perspective(1400px) rotateX(" + (-d * tilt).toFixed(2) + "deg) scale(" + (1 - a * 0.12).toFixed(3) + ")";
        c.style.opacity = (1 - Math.min(a, 1) * 0.45).toFixed(3);
        if (a < bestD) { bestD = a; best = c; }
      }
      var glow = best && best.getAttribute("data-glow");
      if (glow && glow !== lastGlow) {
        lastGlow = glow;
        col.style.setProperty("--sb-glow", glow);
      }
    });
  });

  /* -------------------------------------------------------------------------
     bgscrub: a film fixed BEHIND the page whose frame follows the scroll, while
     the page itself scrolls natively over it. The section is tall; its content
     travels over the canvas. Frames are a numbered WebP sequence (scripts/
     frames.mjs), loaded coarse-to-fine so the film answers early.
     Rest glide (owner's request): data-rest="0.40-0.52,0.80-0.88" names the
     stretches of the film that are ugly to stop on. When the visitor stops
     scrolling inside one, the page glides forward to its end. Never during a
     scroll, never backward, never with reduced motion.
     ------------------------------------------------------------------------- */
  SB.define("bgscrub", function (sec) {
    var canvas = sec.querySelector("[data-sb-bgscrub-canvas]");
    var pattern = sec.getAttribute("data-frames");
    var count = parseInt(sec.getAttribute("data-count"), 10) || 0;
    console.log("⚙️ [Function Started]: bgscrub", count, "frames");
    if (!canvas || !pattern || !count) return;
    if (reduce) { sec.classList.add("is-still"); return; }
    var phone = window.matchMedia("(max-width: 749px)").matches;
    var phonePattern = sec.getAttribute("data-frames-mobile");
    if (phone && phonePattern) pattern = phonePattern;
    var ctx2d = canvas.getContext("2d");
    var frames = new Array(count), shown = -1, want = 0;
    function src(i) { return pattern.replace("{i}", String(i + 1).padStart(3, "0")); }
    function size() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(innerWidth * dpr);
      canvas.height = Math.round(innerHeight * dpr);
      shown = -1;
      draw(want);
    }
    function nearestLoaded(i) {
      for (var d = 0; d < count; d++) {
        if (frames[i - d] && frames[i - d].complete && frames[i - d].naturalWidth) return i - d;
        if (frames[i + d] && frames[i + d].complete && frames[i + d].naturalWidth) return i + d;
      }
      return -1;
    }
    function draw(i) {
      var k = nearestLoaded(i);
      if (k < 0 || k === shown) return;
      shown = k;
      var img = frames[k], cw = canvas.width, ch = canvas.height;
      var s = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      var w = img.naturalWidth * s, h = img.naturalHeight * s;
      ctx2d.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }
    // Coarse to fine: every 8th frame, then every 4th, 2nd, then the rest.
    var order = [];
    [8, 4, 2, 1].forEach(function (stepN) {
      for (var i = 0; i < count; i += stepN) if (order.indexOf(i) < 0) order.push(i);
    });
    var started = false;
    function load() {
      if (started) return;
      started = true;
      var t0 = performance.now(), n = 0;
      order.forEach(function (i) {
        var img = new Image();
        img.decoding = "async";
        img.onload = function () {
          n++;
          if (Math.abs(i - want) < 3) draw(want);
          if (n === count) console.log("✅ [bgscrub]: all", count, "frames in", Math.round(performance.now() - t0), "ms");
        };
        img.onerror = function () { console.error("🛑 [bgscrub]: frame failed", img.src); };
        img.src = src(i);
        frames[i] = img;
      });
    }
    function progress() {
      var r = sec.getBoundingClientRect();
      return clamp(-r.top / Math.max(1, r.height - innerHeight), 0, 1);
    }
    watch(sec, function () {
      load();
      var r = sec.getBoundingClientRect();
      var on = r.top < innerHeight && r.bottom > 0;
      sec.classList.toggle("is-on", on);
      want = Math.round(progress() * (count - 1));
      draw(want);
    });
    window.addEventListener("resize", size, { passive: true });
    size();

    var rests = (sec.getAttribute("data-rest") || "").split(",").map(function (s) {
      var m = s.trim().match(/^([\d.]+)\s*-\s*([\d.]+)$/);
      return m ? [parseFloat(m[1]), parseFloat(m[2])] : null;
    }).filter(Boolean);
    if (rests.length) {
      var idleT, gliding = false;
      var settle = function () {
        if (gliding) return;
        var p = progress();
        var r = sec.getBoundingClientRect();
        if (r.top > 0 || r.bottom < innerHeight) return;
        for (var i = 0; i < rests.length; i++) {
          if (p > rests[i][0] && p < rests[i][1]) {
            var travel = Math.max(1, r.height - innerHeight);
            var dy = Math.ceil((rests[i][1] - p) * travel) + 2;
            console.log("📦 [State Update]: resting on an ugly stretch of the film, gliding forward", dy + "px");
            gliding = true;
            window.scrollBy({ top: dy, behavior: "smooth" });
            setTimeout(function () { gliding = false; }, 900);
            return;
          }
        }
      };
      // A rest is 450ms without a scroll event. 140ms was shorter than the gap between two wheel
      // notches of a slow scroll: the glide started under a visitor still scrolling and a wheel
      // step moved 0px (no-stop probe, 2026-10-05, y=7800 on the gallery).
      var onEnd = function () { clearTimeout(idleT); idleT = setTimeout(settle, 450); };
      window.addEventListener("scroll", onEnd, { passive: true });
    }
  });

  /* -------------------------------------------------------------------------
     pointer: a first screen that answers the cursor.
       glow   a light follows the pointer (CSS variables only)
       field  a starfield that drifts and parts around the pointer (canvas)
       depth  one photo + a grey depth map, displaced toward the pointer (WebGL),
              the landonorris.com trick: 3D-looking from a single picture
     Pointer only: touch and reduced motion get the still picture.
     ------------------------------------------------------------------------- */
  SB.define("pointer", function (box) {
    var mode = box.getAttribute("data-sb-pointer") || "glow";
    console.log("⚙️ [Function Started]: pointer", mode);
    if (reduce || !finePointer) { box.classList.add("is-still"); return; }
    var mx = 0.5, my = 0.5;
    // The words sit over the picture, so the pointer is heard on the whole section, not the picture alone.
    var zone = box.closest("[data-sc-act]") || box.parentElement || box;
    zone.addEventListener("pointermove", function (e) {
      var r = box.getBoundingClientRect();
      mx = (e.clientX - r.left) / r.width;
      my = (e.clientY - r.top) / r.height;
      box.style.setProperty("--sb-mx", mx.toFixed(4));
      box.style.setProperty("--sb-my", my.toFixed(4));
      if (mode === "depth" && box.__sbDepthDraw) box.__sbDepthDraw(mx, my);
    }, { passive: true });
    zone.addEventListener("pointerleave", function () {
      box.style.setProperty("--sb-mx", "0.5");
      box.style.setProperty("--sb-my", "0.5");
      mx = 0.5; my = 0.5;
      if (mode === "depth" && box.__sbDepthDraw) box.__sbDepthDraw(0.5, 0.5);
    });

    if (mode === "field") {
      var cv = box.querySelector("canvas[data-sb-pointer-canvas]");
      if (!cv) return;
      var c2 = cv.getContext("2d"), stars = [], raf = null, live = false, px = -1e4, py = -1e4;
      var dens = parseFloat(box.getAttribute("data-density")) || 0.00018;
      var ink = getComputedStyle(box).getPropertyValue("--sb-field-ink").trim() || "#ffffff";
      var fit = function () {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        cv.width = box.clientWidth * dpr;
        cv.height = box.clientHeight * dpr;
        c2.setTransform(dpr, 0, 0, dpr, 0, 0);
        var n = Math.round(box.clientWidth * box.clientHeight * dens);
        stars = [];
        for (var i = 0; i < n; i++) stars.push({ x: Math.random() * box.clientWidth, y: Math.random() * box.clientHeight, z: Math.random() * 0.9 + 0.1, ox: 0, oy: 0 });
      };
      zone.addEventListener("pointermove", function (e) {
        var r = box.getBoundingClientRect();
        px = e.clientX - r.left;
        py = e.clientY - r.top;
      }, { passive: true });
      var frame = function () {
        var w = box.clientWidth, h = box.clientHeight;
        c2.clearRect(0, 0, w, h);
        c2.fillStyle = ink;
        var tx = (mx - 0.5) * 40, ty = (my - 0.5) * 40;
        for (var i = 0; i < stars.length; i++) {
          var s = stars[i];
          s.y -= 0.08 * s.z;
          if (s.y < -4) s.y = h + 4;
          var dx = s.x - px, dy = s.y - py, d2 = dx * dx + dy * dy, R = 140;
          var push = d2 < R * R ? (1 - Math.sqrt(d2) / R) * 26 * s.z : 0;
          var l = Math.sqrt(d2) || 1;
          s.ox += ((dx / l) * push - s.ox) * 0.12;
          s.oy += ((dy / l) * push - s.oy) * 0.12;
          var x = s.x + tx * s.z + s.ox, y = s.y + ty * s.z + s.oy;
          c2.globalAlpha = 0.25 + s.z * 0.75;
          c2.fillRect(x, y, s.z * 1.8, s.z * 1.8);
        }
        // sb-allow-raf: canvas loop, paused off screen and skipped with reduced motion
        raf = live ? requestAnimationFrame(frame) : null;
      };
      new IntersectionObserver(function (es) {
        live = es[0].isIntersecting;
        // sb-allow-raf: restarts the paused canvas loop when it comes back on screen
        if (live && raf === null) raf = requestAnimationFrame(frame);
      }).observe(box);
      window.addEventListener("resize", fit, { passive: true });
      fit();
    }

    if (mode === "depth") {
      var gl, img = box.querySelector("img[data-sb-depth-color]"), map = box.getAttribute("data-depth-map");
      var cvs = box.querySelector("canvas[data-sb-pointer-canvas]");
      if (!img || !map || !cvs) return;
      try { gl = cvs.getContext("webgl", { premultipliedAlpha: false }); } catch (e) { console.error("🛑 [pointer]: no WebGL", e); }
      if (!gl) return;
      var strength = parseFloat(box.getAttribute("data-strength")) || 0.025;
      var vs = "attribute vec2 p;varying vec2 uv;void main(){uv=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}";
      var fs = "precision mediump float;varying vec2 uv;uniform sampler2D c;uniform sampler2D d;uniform vec2 m;uniform vec2 sc;uniform float k;" +
        "void main(){vec2 u=(uv-.5)*sc+.5;float z=texture2D(d,u).r;vec2 o=(m-.5)*k*(z-.35);gl_FragColor=texture2D(c,u+o);}";
      var prog = gl.createProgram();
      [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]].forEach(function (pair) {
        var s = gl.createShader(pair[0]);
        gl.shaderSource(s, pair[1]);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error("🛑 [pointer]: shader", gl.getShaderInfoLog(s));
        gl.attachShader(prog, s);
      });
      gl.linkProgram(prog);
      gl.useProgram(prog);
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, "p");
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      var tex = function (unit, image) {
        var t = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      };
      var depth = new Image();
      var ready = 0;
      var draw = function (x, y) {
        if (ready < 2) return;
        var aw = cvs.width / cvs.height, ai = img.naturalWidth / img.naturalHeight;
        var sc = aw > ai ? [1, ai / aw] : [aw / ai, 1];
        gl.viewport(0, 0, cvs.width, cvs.height);
        gl.uniform1i(gl.getUniformLocation(prog, "c"), 0);
        gl.uniform1i(gl.getUniformLocation(prog, "d"), 1);
        gl.uniform2f(gl.getUniformLocation(prog, "m"), x, y);
        gl.uniform2f(gl.getUniformLocation(prog, "sc"), sc[0], sc[1]);
        gl.uniform1f(gl.getUniformLocation(prog, "k"), strength);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      };
      var fitGl = function () {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        cvs.width = box.clientWidth * dpr;
        cvs.height = box.clientHeight * dpr;
        draw(mx, my);
      };
      var go = function () {
        ready++;
        if (ready === 2) {
          tex(0, img);
          tex(1, depth);
          box.classList.add("is-live");
          fitGl();
          console.log("✅ [pointer]: depth ready");
        }
      };
      SB.ready(img.currentSrc || img.src, go);
      depth.crossOrigin = "anonymous";
      depth.onload = go;
      depth.onerror = function () { console.error("🛑 [pointer]: depth map failed", map); };
      depth.src = map;
      box.__sbDepthDraw = draw;
      window.addEventListener("resize", fitGl, { passive: true });
    }
  });

  /* -------------------------------------------------------------------------
     slides: still pictures that take turns on their own clock (a setTimeout
     chain, never the scroll), each easing in with a slow zoom. Resting while
     off screen or in a hidden tab. Reduced motion: the first picture holds.
     ------------------------------------------------------------------------- */
  SB.define("slides", function (box) {
    var slides = Array.prototype.slice.call(box.querySelectorAll("[data-sb-slide]"));
    var dots = Array.prototype.slice.call(box.querySelectorAll("[data-sb-slide-dot]"));
    console.log("⚙️ [Function Started]: slides", slides.length);
    if (slides.length < 2) return;
    var every = (parseFloat(box.getAttribute("data-every")) || 6) * 1000;
    var i = 0, timer = null, visible = true;
    function show(n, why) {
      slides[i].classList.remove("is-on");
      if (dots[i]) dots[i].setAttribute("aria-current", "false");
      i = (n + slides.length) % slides.length;
      slides[i].classList.add("is-on");
      if (dots[i]) dots[i].setAttribute("aria-current", "true");
      console.log("📦 [State Update]: slide", i + 1, "of", slides.length, "(" + why + ")");
    }
    function plan() {
      clearTimeout(timer);
      if (reduce || !visible || document.hidden) return;
      timer = setTimeout(function () { show(i + 1, "clock"); plan(); }, every);
    }
    dots.forEach(function (d, n) {
      d.addEventListener("click", function () { console.log("👆 [Trigger]: slide dot", n + 1); show(n, "dot"); plan(); });
    });
    new IntersectionObserver(function (es) { visible = es[0].isIntersecting; plan(); }).observe(box);
    document.addEventListener("visibilitychange", plan);
    slides[0].classList.add("is-on");
    plan();
  });

  /* -------------------------------------------------------------------------
     card modes beyond the CSS swap: cycle (the product's photos take turns while
     the pointer rests on the card) and video (a clip plays under the pointer).
     ------------------------------------------------------------------------- */
  SB.define("card", function (card) {
    var mode = card.getAttribute("data-sb-card");
    if (mode !== "cycle" && mode !== "video") return;
    if (!finePointer || reduce) return;
    var frame = card.querySelector(".sb-card__frame");
    if (!frame) return;
    if (mode === "cycle") {
      var list = [];
      try { list = JSON.parse(card.getAttribute("data-sb-cycle") || "[]"); } catch (e) { console.error("🛑 [card]: cycle list invalid", e); }
      if (list.length < 2) return;
      var layer = document.createElement("img");
      layer.className = "sb-card__img sb-card__img--cycle";
      layer.alt = "";
      frame.appendChild(layer);
      var k = 0, t = null;
      var next = function () {
        k = (k + 1) % list.length;
        SB.ready(list[k], function () { layer.src = list[k]; layer.classList.add("on"); });
        t = setTimeout(next, 1100);
      };
      card.addEventListener("pointerenter", function () { console.log("👆 [Trigger]: card cycle"); k = 0; t = setTimeout(next, 250); });
      card.addEventListener("pointerleave", function () { clearTimeout(t); layer.classList.remove("on"); });
    } else {
      var src = card.getAttribute("data-sb-card-video");
      if (!src) return;
      var v = null;
      card.addEventListener("pointerenter", function () {
        console.log("👆 [Trigger]: card video");
        if (!v) {
          v = document.createElement("video");
          v.muted = true; v.loop = true; v.playsInline = true; v.preload = "auto";
          v.className = "sb-card__img sb-card__video";
          v.src = src;
          frame.appendChild(v);
        }
        v.play().then(function () { v.classList.add("on"); }).catch(function (e) { console.error("🛑 [card]: video refused", e); });
      });
      card.addEventListener("pointerleave", function () { if (v) { v.pause(); v.classList.remove("on"); } });
    }
  });

  /* -------------------------------------------------------------------------
     countdown: only for a real sale with a real end. Hides itself at zero.
     ------------------------------------------------------------------------- */
  SB.define("countdown", function (box) {
    var end = Date.parse(box.getAttribute("data-end") || "");
    console.log("⚙️ [Function Started]: countdown to", box.getAttribute("data-end"));
    if (isNaN(end)) { console.error("🛑 [countdown]: no valid end date"); box.hidden = true; return; }
    var parts = { d: box.querySelector("[data-d]"), h: box.querySelector("[data-h]"), m: box.querySelector("[data-m]"), s: box.querySelector("[data-s]") };
    var pad = function (n) { return String(n).padStart(2, "0"); };
    (function tick() {
      var left = Math.max(0, end - Date.now());
      if (left === 0) { box.hidden = true; console.log("📦 [State Update]: the sale has ended, countdown hidden"); return; }
      var s = Math.floor(left / 1000);
      if (parts.d) parts.d.textContent = Math.floor(s / 86400);
      if (parts.h) parts.h.textContent = pad(Math.floor((s % 86400) / 3600));
      if (parts.m) parts.m.textContent = pad(Math.floor((s % 3600) / 60));
      if (parts.s) parts.s.textContent = pad(s % 60);
      setTimeout(tick, 1000 - (Date.now() % 1000));
    })();
  });

  /* -------------------------------------------------------------------------
     dye: a new colour rising through the garment from the hem (motion.md §4).
     Transforms only; the plate colour is baked into the photographs, so there
     is no blend mode anywhere (traps.md §1). Used by the product page when
     data-sb-colour-change="dye".
     ------------------------------------------------------------------------- */
  SB.dye = function (stage, src, alt) {
    var base = stage.querySelector("[data-sb-stage-base]");
    if (!base || !src || base.getAttribute("src") === src) return;
    var wrap = stage.querySelector("[data-sb-dye]");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "sb-dye";
      wrap.setAttribute("data-sb-dye", "");
      wrap.setAttribute("aria-hidden", "true");
      wrap.innerHTML = '<img class="sb-dye__img" alt="">';
      stage.appendChild(wrap);
    }
    var img = wrap.querySelector("img");
    clearTimeout(stage.__sbDye);
    SB.ready(src, function () {
      wrap.classList.remove("on");
      img.src = src;
      void wrap.offsetWidth; // the resting style is computed; the class change transitions by itself
      wrap.classList.add("on");
      stage.__sbDye = setTimeout(function () {
        base.removeAttribute("srcset");
        base.src = src;
        if (alt !== undefined) base.alt = alt;
        stage.__sbDye = setTimeout(function () { wrap.classList.remove("on"); console.log("✅ [dye]: at rest", src); }, 60);
      }, reduce ? 20 : 820);
    });
  };

  /* -------------------------------------------------------------------------
     boot
     ------------------------------------------------------------------------- */
  function mountEngine(root) {
    if (!window.ScrollCraft) {
      console.error("🛑 [sb-motion]: the motion engine (scrollcraft.js) is missing");
      return;
    }
    window.ScrollCraft.mount(root);
  }

  function boot() {
    console.log("🚀 [Boot]: sb-motion", Object.keys(effects).length, "effects, reduced motion:", reduce);
    // [data-sb-product] marks the product section; its effect name is "product".
    mountEngine(document.body);
    SB.arm(document);
    console.log("✅ [Boot]: armed");
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // The theme editor re-renders one section at a time.
  document.addEventListener("shopify:section:load", function (e) {
    console.log("🔄 [Editor]: section reloaded", e.detail && e.detail.sectionId);
    mountEngine(e.target);
    SB.arm(e.target);
  });
  document.addEventListener("shopify:block:select", function (e) {
    console.log("👆 [Editor]: block selected", e.detail && e.detail.blockId);
    if (e.target && e.target.scrollIntoView) e.target.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  });
})();
